import type { JSX, KeyboardEvent } from 'react'
import type { IInputProps } from 'atoms/Input'

import { useCallback, useRef } from 'react'
import { ActionButton, ActionButtonColors } from 'atoms/ActionButton'
import {
  Button,
  ButtonFonts,
  ButtonIconSizes,
  IconPositions,
} from 'atoms/Button'
import { Icon, IconNames, IconSizes } from 'atoms/Icon'
import {
  InputActionButtonPosition,
  InputBackgroundColors,
  InputBorderColors,
  InputBorders,
  InputCorners,
  InputFontColors,
  InputFonts,
  InputNames,
  InputPaddings,
} from 'atoms/Input'
import { TextBox, TextBoxColors, TextBoxTypes } from 'atoms/TextBox'
import {
  SearchInputLabelSC,
  SearchInputSC,
  SearchInputWrapperSC,
  SearchWrapperSC,
} from './SearchInput.styled'

const TYPE = 'search'

export interface ISearchInputProps extends Omit<
  IInputProps,
  'type' | 'secured' | 'checked' | 'defaultChecked'
> {
  isNativeDeleteButtonShown?: boolean
  searchIcon?: boolean
  searchIconSize?: IconSizes
}

export function SearchInput({
  ref: innerRef,
  children,
  id,
  name = InputNames.Search,
  pattern,
  value,
  defaultValue,
  minLength,
  maxLength,
  placeholder = '',
  placeholderColor = InputFontColors.TEXT_ICON_PRIMARY,
  font = InputFonts.Body3,
  colorDefault = InputFontColors.TEXT_ICON_PRIMARY,
  colorDisabled = InputFontColors.TEXT_ICON_TERTIARY,
  label = '',
  labelFont = TextBoxTypes.Caption3,
  labelColorDefault = TextBoxColors.TEXT_ICON_SECONDARY,
  inputLabelWidth,
  inputWrapperWidth,
  inputWrapperHeight,
  height,
  icon,
  iconSize = ButtonIconSizes.small,
  iconFont = ButtonFonts.Button1,
  actionButtonPosition = InputActionButtonPosition.Right,
  paddings = InputPaddings.SIZE_0,
  inputLabelPaddings,
  inputLabelMargins,
  corners = InputCorners.CORNERS_10,
  required = false,
  disabled,
  invalid,
  borders = InputBorders.BORDER_0,
  borderColorDefault = InputBorderColors.SURFACE_OPACITY_LIGHT_10,
  borderColorHovered = InputBorderColors.SURFACE_OPACITY_LIGHT_15,
  borderColorPressed = InputBorderColors.SURFACE_OPACITY_LIGHT_20,
  borderColorInvalid = InputBorderColors.ADDITIONAL_RED_2,
  backgroundColorDefault = InputBackgroundColors.SURFACE_OPACITY_LIGHT_10,
  backgroundColorHovered = InputBackgroundColors.SURFACE_OPACITY_LIGHT_15,
  backgroundColorPressed = InputBackgroundColors.SURFACE_OPACITY_LIGHT_20,
  actionButtonColorDefault = ActionButtonColors.COLOR_MAIN_DEFAULT,
  actionButtonColorHovered = ActionButtonColors.SURFACE_LIGHT_2,
  actionButtonColorPressed = ActionButtonColors.COLOR_MAIN_PRESSED,
  actionButtonPaddings,
  actionButtonIndent,
  caretColor,
  autoComplete,
  autoFocus = true,
  isNativeDeleteButtonShown,
  onChange,
  onBlur,
  onKeyDown,
  onClick,
  searchIcon = true,
  voiceSearch = false,
  onVoiceSearch,
  searchIconSize = IconSizes.small,
}: ISearchInputProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
      }
      onKeyDown?.(e)
    },
    [onKeyDown]
  )

  const handleClick = useCallback((e) => {
    // Used to autofocus input when click on action button
    if (icon === IconNames.search) {
      inputRef?.current?.focus()
    }
    onClick?.(e)
  }, [])

  const isVisibleDeleteButton = icon && value

  return (
    <SearchInputLabelSC
      inputLabelWidth={inputLabelWidth}
      backgroundColorPressed={backgroundColorPressed}
      borderColorPressed={borderColorPressed}
      inputLabelPaddings={inputLabelPaddings}
      inputLabelMargins={inputLabelMargins}
    >
      {searchIcon && <Icon name={IconNames.search} size={searchIconSize} />}
      <TextBox title={label} font={labelFont} color={labelColorDefault} />

      <SearchWrapperSC>
        <SearchInputWrapperSC
          inputWrapperWidth={inputWrapperWidth}
          inputWrapperHeight={inputWrapperHeight}
          actionButtonPosition={actionButtonPosition}
          actionButtonIndent={actionButtonIndent}
        >
          <SearchInputSC
            ref={innerRef || inputRef}
            id={id}
            type={TYPE}
            name={name}
            pattern={pattern}
            value={value}
            defaultValue={defaultValue}
            minLength={minLength}
            maxLength={maxLength}
            placeholder={placeholder}
            placeholderColor={placeholderColor}
            font={font}
            colorDefault={colorDefault}
            colorDisabled={colorDisabled}
            inputHeight={height}
            paddings={paddings}
            corners={corners}
            required={required}
            disabled={disabled}
            invalid={invalid}
            borders={borders}
            borderColorDefault={borderColorDefault}
            borderColorHovered={borderColorHovered}
            borderColorPressed={borderColorPressed}
            borderColorInvalid={borderColorInvalid}
            backgroundColorDefault={backgroundColorDefault}
            backgroundColorHovered={backgroundColorHovered}
            backgroundColorPressed={backgroundColorPressed}
            icon={icon}
            actionButtonPosition={actionButtonPosition}
            caretColor={caretColor}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            isNativeDeleteButtonShown={isNativeDeleteButtonShown}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
          />
          {isVisibleDeleteButton && (
            <ActionButton
              font={iconFont || ButtonFonts.Button2}
              icon={icon}
              iconSize={iconSize}
              colorDefault={actionButtonColorDefault}
              colorHovered={actionButtonColorHovered}
              colorPressed={actionButtonColorPressed}
              paddings={actionButtonPaddings}
              onClick={handleClick}
              tabIndex={-1}
            />
          )}
          {children}
        </SearchInputWrapperSC>

        {voiceSearch && (
          <Button
            icon={IconNames.microphone}
            onClick={onVoiceSearch}
            iconPosition={IconPositions.Center}
          />
        )}
      </SearchWrapperSC>
    </SearchInputLabelSC>
  )
}
